/* pure-timeline -- scroll-linked marker colour for the /education history band.
   Section-scoped custom element, idempotent define, cleans up on disconnect.

   This is a port of one Webflow IX2 interaction, recovered from the export's
   `js/webflow.js` rather than eyeballed. The record is event `e-648` and action
   list `a-324` "Timeline Circle [Scroll]":

     event   SCROLLING_IN_VIEW -> GENERAL_CONTINUOUS_ACTION
     target  .timeline11_circle, appliesTo CLASS, all four media queries
     config  smoothing 50, startsEntering true, startsExiting false,
             addStartOffset false, addEndOffset false
     a-324   keyframe 45 -> STYLE_BACKGROUND_COLOR rgb(217,218,219)
             keyframe 50 -> STYLE_BACKGROUND_COLOR rgb(0,0,0)

   Three things about it are the opposite of what the rendered page suggests,
   and all three are load-bearing:

   1. The RESTING state is grey, and it is not in any stylesheet. Webflow's CSS
      paints `.timeline11_circle` `#000` -- the END of the ramp. IX2 writes the
      grey inline on every circle at init. So the stylesheet keeps `#000` here
      too and this file writes the grey, which also means our no-JS rendering is
      live's no-JS rendering: eleven black markers.

   2. It is scroll-POSITION linked, not a timed transition. There is no
      duration to port. `duration: 500` sits in the action config and is dead --
      a continuous action never reads it.

   3. Its only time constant is the `smoothing`, which IX2 applies as a
      geometric decay per ANIMATION FRAME, not per millisecond:
      `position += (target - position) * (1 - smoothing/100)`. Measured back off
      live with `motion-fit.mjs --model=exp`, the remaining distance halves
      every frame -- k = 0.503, rms 0.0018 -- which is the config's 50 exactly.
      The best bezier fit for the same samples is `easeOutExpo 100ms` at rms
      0.0225, an order of magnitude worse AND only true at 60fps.

   The progress line needs no script at all: it is `position: sticky` plus a
   white cover, and lives entirely in pure-editorial.css. */
if (!customElements.get('pure-timeline')) {
  customElements.define(
    'pure-timeline',
    class extends HTMLElement {
      connectedCallback() {
        // `:scope >` throughout: a repeated item can contain a nested copy of
        // itself and querySelector finds the nested one first.
        this.markers = Array.from(this.querySelectorAll(':scope > ol > li > .pure-history__marker'));
        if (!this.markers.length) return;

        this.positions = new Array(this.markers.length).fill(0);
        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.tick = this.tick.bind(this);
        this.kick = this.kick.bind(this);
        this.frame = 0;

        // IX2 writes the resting state and `will-change` inline at init and
        // starts every instance at position 0, so a marker already past the
        // threshold on load converges from grey rather than starting black.
        // Reproduced rather than short-circuited: it is a visible ~100ms
        // transient on first paint at the top of the page.
        this.markers.forEach((marker) => {
          marker.style.willChange = 'background';
          this.paint(marker, 0);
        });

        window.addEventListener('scroll', this.kick, { passive: true });
        window.addEventListener('resize', this.kick, { passive: true });
        if (this.reduced.addEventListener) this.reduced.addEventListener('change', this.kick);
        this.kick();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.kick);
        window.removeEventListener('resize', this.kick);
        if (this.reduced && this.reduced.removeEventListener) this.reduced.removeEventListener('change', this.kick);
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
      }

      /* Start the loop if it is not already running. IX2 keeps a frame loop
         alive for the life of the session; this one stops once every marker has
         converged, which is the same rendering with no idle frame cost. */
      kick() {
        if (!this.frame) this.frame = requestAnimationFrame(this.tick);
      }

      tick() {
        // IX2 reads the scroll element's clientHeight, not window.innerHeight.
        // They differ by the horizontal scrollbar, which is exactly the case
        // where a band that overflows would shift its own thresholds.
        const viewport = document.documentElement.clientHeight;
        // Reduced motion drops the lag, not the state change: the marker still
        // reports how far down the timeline you are, it just stops trailing.
        const follow = this.reduced.matches ? 1 : 0.5;
        let settled = true;

        this.markers.forEach((marker, index) => {
          const rect = marker.getBoundingClientRect();
          const span = viewport + rect.height;
          const target = span > 0 ? Math.min(Math.max(0, viewport - rect.top), span) / span : 0;
          let position = this.positions[index] + (target - this.positions[index]) * follow;
          // Terminate the geometric decay well below anything that can paint:
          // 1e-5 of progress is a thousandth of one sRGB channel across the
          // five-point ramp, and it is reached in about 17 frames.
          if (Math.abs(target - position) < 0.00001) position = target;
          else settled = false;
          this.positions[index] = position;
          this.paint(marker, position);
        });

        this.frame = settled ? 0 : requestAnimationFrame(this.tick);
      }

      /* The two keyframes, blended linearly per sRGB channel. Measured against
         live at eleven stops across the band, the largest deviation from a
         linear blend is 0.8 of one channel -- i.e. rounding. */
      paint(marker, position) {
        const percent = position * 100;
        let colour;
        if (percent <= 45) colour = [217, 218, 219];
        else if (percent >= 50) colour = [0, 0, 0];
        else {
          const blend = (percent - 45) / 5;
          colour = [217, 218, 219].map((channel) => Math.round(channel - channel * blend));
        }
        marker.style.backgroundColor = `rgb(${colour[0]}, ${colour[1]}, ${colour[2]})`;
      }
    }
  );
}
